import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, CalendarCheck, MessageCircle, Linkedin, Instagram, Facebook } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND, SERVICE_OPTIONS, GMAIL_COMPOSE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

const SOCIALS = [
  { key: "linkedin", href: BRAND.linkedin, Icon: Linkedin, label: "LinkedIn" },
  { key: "instagram", href: BRAND.instagram, Icon: Instagram, label: "Instagram" },
  { key: "facebook", href: BRAND.facebook, Icon: Facebook, label: "Facebook" },
  { key: "whatsapp", href: `https://wa.me/${BRAND.whatsapp.replace(/[^0-9]/g, "")}`, Icon: MessageCircle, label: "WhatsApp" },
  { key: "email", href: GMAIL_COMPOSE, Icon: Mail, label: "Email" },
];

export default function Contact() {
  usePageMeta(
    `Contact — ${BRAND.name}`,
    "Book a free consultation with a 40-year export-import veteran and AI strategist. Trade, training, or digital — let's talk."
  );

  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please select the service you're interested in.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry has been received. I'll get back to you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again, or reach out directly by email or phone.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-12 border-[#1E3A5F] bg-[#051320] text-slate-200 placeholder:text-slate-600 focus-visible:ring-gold";

  return (
    <div data-testid="contact-page">
      <PageHero
        kicker="Contact"
        lines={["Let's Start the", "Conversation."]}
        description="Tell me a little about your business and what you're trying to achieve. Every enquiry gets a personal reply — from me, not an assistant."
        accent="gold"
      />

      <section className="pb-28 pt-8 md:pb-36">
        <div className="mx-auto grid max-w-6xl gap-14 px-6 lg:grid-cols-12">
          {/* FORM */}
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8 md:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">Name *</Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-300">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 ..."
                    className={inputCls}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Service Interested In *</Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="contact-service-select" className={inputCls}>
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="border-[#1E3A5F] bg-[#0A2540]">
                      {SERVICE_OPTIONS.map((o) => (
                        <SelectItem
                          key={o.value}
                          value={o.value}
                          data-testid={`contact-service-option-${o.value}`}
                          className="text-slate-200 focus:bg-white/5 focus:text-white"
                        >
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message" className="text-slate-300">Message *</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell me about your business and what you'd like to achieve..."
                    className="border-[#1E3A5F] bg-[#051320] text-slate-200 placeholder:text-slate-600 focus-visible:ring-gold"
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="btn-primary mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          </Reveal>

          {/* INFO */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <a
                href={BRAND.bookingLink}
                data-testid="book-call-button"
                className="group flex items-center justify-between rounded-2xl border border-gold/40 bg-gold/5 p-7 transition-[background-color,border-color] duration-300 hover:border-gold hover:bg-gold/10"
              >
                <div className="flex items-center gap-4">
                  <CalendarCheck className="h-7 w-7 text-gold" strokeWidth={1.5} />
                  <div>
                    <p className="font-serif text-xl text-white">Book a Call Directly</p>
                    <p className="mt-1 text-xs text-slate-500">[Replace with your Calendly / booking link]</p>
                  </div>
                </div>
              </a>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="mt-6 space-y-4 rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-7">
                <h3 className="font-serif text-xl text-white">Reach Me Directly</h3>
                <a
                  href={`https://wa.me/${BRAND.whatsapp.replace(/[^0-9]/g, "")}`}
                  data-testid="contact-whatsapp-link"
                  className="flex items-center gap-4 text-sm text-slate-300 transition-colors hover:text-gold"
                >
                  <MessageCircle className="h-5 w-5 shrink-0 text-gold/70" strokeWidth={1.5} />
                  WhatsApp: {BRAND.whatsapp}
                </a>
                <a
                  href={`tel:${BRAND.phone}`}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 text-sm text-slate-300 transition-colors hover:text-gold"
                >
                  <Phone className="h-5 w-5 shrink-0 text-gold/70" strokeWidth={1.5} />
                  {BRAND.phone}
                </a>
                <a
                  href={GMAIL_COMPOSE}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 text-sm text-slate-300 transition-colors hover:text-gold"
                >
                  <Mail className="h-5 w-5 shrink-0 text-gold/70" strokeWidth={1.5} />
                  {BRAND.email}
                </a>
                <p className="flex items-start gap-4 text-sm text-slate-300" data-testid="contact-address">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold/70" strokeWidth={1.5} />
                  {BRAND.address}
                </p>
                <div className="flex gap-3 border-t border-[#1E3A5F] pt-5" data-testid="contact-socials">
                  {SOCIALS.map(({ key, href, Icon, label }) => (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-${key}-icon`}
                      aria-label={label}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1E3A5F] text-slate-400 transition-colors hover:border-gold hover:text-gold"
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.5} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.26}>
              <div className="mt-6 rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-7">
                <p className="font-serif text-lg italic leading-relaxed text-slate-300">
                  "Every enquiry is read and answered personally. Expect a reply within one working
                  day — usually much sooner."
                </p>
              </div>
            </Reveal>
          </div>

          {/* MAP */}
          <Reveal delay={0.15} className="lg:col-span-12">
            <div className="overflow-hidden rounded-2xl border border-[#1E3A5F]" data-testid="contact-map">
              <iframe
                title="EXIM INDIA office location — Vastral, Ahmedabad"
                src="https://www.google.com/maps?q=Satva+Icon,+Vastral+Cross+Road,+S.P.+Ring+Road,+Vastral,+Ahmedabad-382418,+Gujarat,+India&output=embed"
                className="h-80 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
